import React, { useEffect, useMemo } from 'react';
import useDeviceType from '../../hooks/useDeviceType';
import { BorrowerDto } from '../../openapi/atlantis';
import { OnboardingResponseRoleEnum } from '../../openapi/yenta';
import { fetchApplicationAnswers } from '../../slices/ApplicationSlice';
import { fetchDropboxById } from '../../slices/DropboxSlice';
import { useAppDispatch, useAppSelector } from '../../slices/store';
import { cn } from '../../utils/classUtils';
import {
  getLegacyApplicationId,
  getQuestionAnswers,
} from '../../utils/OnboardingHelpers';
import DefaultLoader from '../DefaultLoader';
import { ReviewItem } from './ReviewItem';

interface ReviewApplicationProps {
  title?: string;
  onNavigateToQuestion?: (questionId: string) => Promise<void>;
}

const ReviewApplication: React.FC<ReviewApplicationProps> = ({
  title = 'Review Application',
  onNavigateToQuestion,
}) => {
  const dispatch = useAppDispatch();
  const { isMobile } = useDeviceType();
  const {
    application: { progress, questionAnswers },
    auth: { authUserDetail },
    dropbox: { dropboxByBorrowerId },
  } = useAppSelector((state) => state);

  // Get dropbox data for the current user
  const dropbox = dropboxByBorrowerId[authUserDetail?.id!];

  const legacyApplicationId = getLegacyApplicationId(
    authUserDetail,
    OnboardingResponseRoleEnum.Agent,
  );

  // Fetch answers and dropbox files when component mounts
  useEffect(() => {
    if (legacyApplicationId) {
      dispatch(fetchApplicationAnswers(legacyApplicationId!));
    }

    // Fetch dropbox files if user has a dropboxId
    if (authUserDetail?.dropboxId && authUserDetail?.id) {
      const borrowerData: BorrowerDto = {
        id: authUserDetail.id,
        dropboxId: authUserDetail.dropboxId,
      } as BorrowerDto;
      dispatch(fetchDropboxById(borrowerData));
    }
  }, [dispatch, legacyApplicationId, authUserDetail]);

  // Get completed questions from Redux
  const completedQuestions = useMemo(() => {
    return (
      progress?.sectionProgresses?.flatMap(
        (section) =>
          section.questionProgresses
            ?.filter((q) => q.status === 'COMPLETED' && q.id && q.title)
            ?.map((q) => ({
              id: q.id!,
              label: q.title!,
              value: getQuestionAnswers(q.id!, questionAnswers, dropbox),
            })),
      ) || []
    );
  }, [progress?.sectionProgresses, questionAnswers, dropbox]);

  const fieldItems = useMemo(
    () =>
      completedQuestions.map((field, index) => ({
        field,
        index: index + 1,
        isLast: index === completedQuestions.length - 1,
      })),
    [completedQuestions],
  );

  if (!questionAnswers) {
    return <DefaultLoader />;
  }

  return (
    <div>
      <h2 className='text-2xl font-semibold text-center pb-6 font-inter'>
        {title}
      </h2>
      <div
        data-testid='scroll-container'
        className={cn(
          'overflow-y-auto pb-24',
          isMobile ? 'max-h-[calc(100vh-240px)]' : 'max-h-[calc(100vh-340px)]',
        )}
      >
        {fieldItems.map(({ field, index, isLast }) => (
          <ReviewItem
            key={field?.id!}
            field={field!}
            index={index}
            onEdit={async () => {
              if (onNavigateToQuestion) {
                await onNavigateToQuestion(field?.id!);
              }
            }}
            isLast={isLast}
          />
        ))}
      </div>
    </div>
  );
};

export default ReviewApplication;
